import { Box, SxProps, Theme, Typography } from "@mui/material";
import { WorkIcon, ZapIcon } from "../../../../shared/images/icons";
import { textSM, textXS } from "../../../../shared/constants";
import colors from "../../../../shared/constants/color";
import { AIScoreCircle } from "../shared/AIScoreCircle";
import { WorkingModelTag } from "../shared/WorkingModelTag";
import { ProjectStatusTag } from "../shared/ProjectStatusTag";
import { PriorityTag } from "../shared/PriorityTag";
import { UsersIcon, MapPinIcon } from "../../../../shared/images/icons";
import { Button, AILoading, XCard } from "../../../../components";
import type {
  PositionInfo,
  AIPositionInfo,
} from "../../../interviewAssistant/InterviewAssistant";
import { useWiserTheme } from "../../../../hooks/useWiserTheme";
import { getSidebarIconFilter } from "../../../../shared/theme/wiserThemeHelpers";
import {
  centeredContentPy4Sx,
  centeredEmptyStateSx,
  columnGap12Sx,
  rowAlignCenterGap8Sx,
  rowAlignCenterGap8WrapSx,
  rowAlignCenterGap16WrapSx,
  getIcon14Style,
  matchEvalHeaderSx,
  matchEvalDescriptionSx,
  matchEvalEmptySx,
} from "../../style";

interface IProps {
  candidateName: string;
  position: PositionInfo | null;
  positionId: string | null;
  matchedPosition: AIPositionInfo | null;
  onPositionDetailClick?: () => void;
  onPositionDescriptionClick?: () => void;
  loading?: boolean;
  sx?: SxProps<Theme>;
}

export const PositionDetailCard = ({
  candidateName,
  position,
  positionId,
  matchedPosition,
  onPositionDetailClick,
  onPositionDescriptionClick,
  loading = false,
  sx,
}: IProps) => {
  const { darkModeActive, tokens } = useWiserTheme();
  const iconFilter = getSidebarIconFilter(darkModeActive);

  if (loading) {
    return (
      <XCard
        title="Görüşülen Pozisyon"
        icon={<img src={WorkIcon} />}
        style="neutral"
        sx={sx}
      >
        <Box sx={centeredContentPy4Sx}>
          <AILoading />
        </Box>
      </XCard>
    );
  }
  if (!position) {
    return (
      <XCard
        title="Görüşülen Pozisyon"
        icon={<img src={WorkIcon} />}
        style="neutral"
        sx={sx}
      >
        <Box sx={centeredEmptyStateSx}>
          <Typography
            sx={textXS(
              400,
              darkModeActive ? tokens.textMuted : colors.neutral.N500,
            )}
          >
            Pozisyon bilgisi bulunamadı
          </Typography>
        </Box>
      </XCard>
    );
  }

  return (
    <XCard
      title="Görüşülen Pozisyon"
      icon={<img src={WorkIcon} />}
      style="neutral"
      footerAction={
        <>
          <Button
            variant="texted"
            size="md"
            color={colors.blue.B300}
            fontWeight="500"
            onClick={onPositionDetailClick}
          >
            Pozisyon Detay
          </Button>
          <Button
            variant="texted"
            size="md"
            color={colors.blue.B300}
            fontWeight="500"
            onClick={onPositionDescriptionClick}
          >
            Pozisyon Açıklama
          </Button>
        </>
      }
      sx={sx}
    >
      <Box sx={columnGap12Sx}>
        {/* Position Title and Location */}
        <Box sx={rowAlignCenterGap8WrapSx}>
          <Typography
            sx={textSM(
              500,
              darkModeActive ? tokens.textPrimary : colors.neutral.N800,
            )}
          >
            {(position.techGroup ?? []).map((tg) => tg.techGroupName).join(" - ")}
          </Typography>
          <WorkingModelTag workingModel={position.workingModelName || ""} />
        </Box>

        {/* Client and Team */}
        <Typography
          sx={textXS(
            400,
            darkModeActive ? tokens.textSecondary : colors.neutral.N500,
          )}
        >
          {position.clientName?.toTitleCaseTR() || ""} ·{" "}
          {position.teamName?.toTitleCaseTR() || ""}
        </Typography>

        {/* Status and Priority Tags */}
        <Box sx={rowAlignCenterGap8WrapSx}>
          <ProjectStatusTag status={position.statusName} />
          <PriorityTag priority={position.priorityId} />
        </Box>

        {/* Candidate Count and Location */}
        <Box sx={rowAlignCenterGap16WrapSx}>
          <Box sx={rowAlignCenterGap8Sx}>
            <img
              src={UsersIcon}
              alt=""
              style={getIcon14Style(iconFilter)}
            />
            <Typography
              sx={textXS(
                400,
                darkModeActive ? tokens.textSecondary : colors.neutral.N500,
              )}
            >
              {position.headCount} Aday
            </Typography>
          </Box>
          <Box sx={rowAlignCenterGap8Sx}>
            <img
              src={MapPinIcon}
              alt=""
              style={getIcon14Style(iconFilter)}
            />
            <Typography
              sx={textXS(
                400,
                darkModeActive ? tokens.textSecondary : colors.neutral.N500,
              )}
            >
              {(position.location ?? []).map((loc) => loc.locationName).join(" - ")}
            </Typography>
          </Box>
        </Box>

        {/* Match Evaluation */}
        <XCard style="aiWhite">
          {/* Score Badge and Question */}
          <Box sx={matchEvalHeaderSx}>
            {matchedPosition ? (
              <AIScoreCircle
                score={matchedPosition.matchScore}
                uniqueId={`position-${positionId ?? matchedPosition.positionId}`}
              />
            ) : (
              <img src={ZapIcon} />
            )}
            <Typography sx={textSM(500, "aiGradient")}>
              {candidateName} bu işle nasıl eşleşiyor?
            </Typography>
          </Box>

          {/* Description */}
          {matchedPosition ? (
            <Box sx={matchEvalDescriptionSx}>
              <Typography
                sx={textXS(
                  400,
                  darkModeActive ? tokens.textSecondary : colors.neutral.N500,
                )}
              >
                {matchedPosition.explanation}
              </Typography>
            </Box>
          ) : (
            <Box sx={matchEvalEmptySx}>
              <Typography sx={textXS(400, colors.neutral.N500)}>
                Yeterli veri olmadığı için AI pozisyon değerlendirmesi
                yapılamadı.
              </Typography>
            </Box>
          )}
        </XCard>
      </Box>
    </XCard>
  );
};
